import { HttpResponse } from '@angular/common/http';

/** A file the app fetched: the bytes, the name the server gave it, and its type. */
export interface IDownloadedFile {
  blob: Blob;
  filename: string;
  contentType: string;
}

/**
 * The name the server put in `Content-Disposition`, or null when it said none.
 *
 * All three forms of RFC 6266 are read, in the order that RFC gives them:
 * `filename*` (percent-encoded, the only form that carries the accents an
 * fr/it document name is full of) wins over plain `filename`, quoted or bare.
 * Reading only the plain parameter is not "one form unsupported": the naive
 * `filename=` match happily matches the `*=` spelling and returns
 * `*=UTF-8''Caf%C3%A9.pdf` as the name.
 *
 * Every route in the estate serves the quoted form. Reading the other two makes
 * adding one a server change, not a silent rename here.
 */
export function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;

  const extended = header.match(/filename\*\s*=\s*(?:[\w.-]+'[^']*')?([^;\n]+)/i);
  if (extended) {
    const value = extended[1].trim().replace(/^"|"$/g, '');
    try {
      // Only UTF-8 and ISO-8859-1 are legal charsets here, and decodeURIComponent
      // reads the first. A malformed sequence throws — the raw value is still a
      // better name than none.
      return decodeURIComponent(value) || null;
    } catch {
      return value || null;
    }
  }

  const plain = header.match(/filename\s*=\s*"?([^";\n]+)"?/i);
  return plain?.[1].trim() || null;
}

/**
 * The three fields every blob route in the app hands back.
 *
 * Written out per route, each one picks its own default for a missing type and
 * its own regex for the header — and the previewer, which decides how to render
 * from the name, then behaves differently per route.
 */
export function toDownloadedFile(response: HttpResponse<Blob>, fallbackName = ''): IDownloadedFile {
  return {
    blob: response.body ?? new Blob(),
    filename: filenameFromContentDisposition(response.headers.get('Content-Disposition')) ?? fallbackName,
    contentType: response.headers.get('Content-Type') ?? 'application/octet-stream',
  };
}
